/** @format */

import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import {
  Chain,
  Environment,
  ProductAvailabilityCollection,
  ProductCatalogInput,
  ProductPromotionCollection,
  ProductVO,
} from '@shared/data-access/interfaces';
import { OrchestratedProductAttributes } from '@shared/favorites/data-access/interfaces';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { KillswitchService } from './killswitch/killswitch.service';

interface ProductCatalogHttpOptions {
  headers?: HttpHeaders;
  params?: HttpParams;
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ProductCatalogService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(Environment);
  private readonly chain = inject(Chain);
  protected platformId = inject(PLATFORM_ID);
  private readonly killswitchService = inject(KillswitchService);
  private readonly isProductCatalogBrandDomainsEnabled =
    this.killswitchService.killSwitchForCurrentChain('useProductCatalogBrandDomains');

  private getListOfProductsRequestConfig(queryParams: ProductCatalogInput): {
    url: string;
    options: ProductCatalogHttpOptions;
  } | null {
    const params = encodeURIComponent(JSON.stringify(queryParams));
    const elastic = this.environment.api.elastic;

    if (this.isProductCatalogBrandDomainsEnabled()) {
      if (!elastic?.searchTermUrlByChain) {
        return null;
      }
      // No leading slash here: searchTermUrlByChain already ends in "/", unlike searchTermUrl below. Don't "fix" one to match the other.
      return {
        url: `${elastic.searchTermUrlByChain}v2/search?searchVO=${params}`,
        options: {
          headers: new HttpHeaders({ channel: this.chain.chainIdentifierAbbr }),
          withCredentials: true,
        },
      };
    }

    if (!elastic?.searchTermUrl) {
      return null;
    }

    return {
      url: `${elastic.searchTermUrl}/v2/search?searchVO=${params}`,
      options: {
        headers: new HttpHeaders({
          ['Content-Type']: 'application/json',
          channel: this.chain.chainIdentifierAbbr,
        }),
      },
    };
  }

  /**
   * @description
   * Get list of products from the product catalog api
   * @param queryParams params for the call, see ProductCatalogInput for reference
   */
  getListOfProducts$(queryParams: ProductCatalogInput) {
    const config = this.getListOfProductsRequestConfig(queryParams);
    if (!config) {
      return throwError(() => new Error('Elastic product catalog URL is not configured'));
    }
    const { url, options } = config;

    // Make the HTTP Request
    return this.http.get(url, options).pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * @description
   * Get availability information for each ecode in the product list
   * @param productVOs
   * @param storeZipCode Zipcode of the currently selected store
   * @param selectedStoreId Id of the currently selected store
   * @param boplExperienceEnabled Feature flag for bopl
   */
  getProductListAvailabilityData(
    productVOs: ProductVO[],
    boplExperienceEnabled: boolean,
    storeZipCode?: string,
    selectedStoreId?: string,
  ): Observable<ProductAvailabilityCollection | null> {
    if (!productVOs?.length) {
      return of(null);
    }

    const ecodeList: string[] = productVOs
      .filter((item) => item?.parentPartnumber)
      .map((item) => item.parentPartnumber);

    return this.getAvailabilityData(
      ecodeList,
      boplExperienceEnabled,
      storeZipCode,
      selectedStoreId,
    );
  }

  private getAvailabilityRequestConfig(): {
    url: string;
    options: ProductCatalogHttpOptions;
  } | null {
    const elastic = this.environment.api.elastic;

    if (this.isProductCatalogBrandDomainsEnabled()) {
      if (!elastic?.searchTermUrlByChain) {
        return null;
      }
      return {
        url: `${elastic.searchTermUrlByChain}v1/inventory/messages`,
        // No custom headers: keeps this a CORS-simple request and avoids an extra preflight round trip.
        options: { withCredentials: true },
      };
    }

    if (!elastic?.searchTermUrl) {
      return null;
    }

    return {
      url: `${elastic.searchTermUrl}v1/inventory/messages`,
      options: {},
    };
  }

  getAvailabilityData(
    ecodeList: string[],
    boplExperienceEnabled: boolean,
    storeZipCode?: string,
    selectedStoreId?: string,
  ): Observable<ProductAvailabilityCollection | null> {
    // Checking again in case none of the productVOs in the argument had a parentPartNumber
    if (!ecodeList.length) {
      return of(null);
    }

    const config = this.getAvailabilityRequestConfig();
    if (!config) {
      return throwError(() => new Error('Elastic searchTermUrl is not configured'));
    }
    const { url: inventoryRequest, options } = config;

    const params = {
      ecodeList,
      ...(selectedStoreId && { selectedStore: selectedStoreId }),
      ...(storeZipCode && { zipCode: storeZipCode }),
      ...(boplExperienceEnabled && { bopl: 'true' }),
    };

    const httpParams = new HttpParams().appendAll(params);

    const httpOptions: ProductCatalogHttpOptions = { ...options, params: httpParams };

    // Make the HTTP Request
    return this.http.get<ProductAvailabilityCollection>(inventoryRequest, httpOptions).pipe(
      timeout(5000),
      catchError((error) => throwError(() => error)),
    );
  }

  private getFavoritesAvailabilityRequestConfig(): {
    url: string;
    options: ProductCatalogHttpOptions;
  } | null {
    const headers = new HttpHeaders({ 'X-TIMEOUT': `${DCSG_CONSTANTS.timeouts.xshort}` });
    const elastic = this.environment.api.elastic;

    if (this.isProductCatalogBrandDomainsEnabled()) {
      if (!elastic?.searchTermUrlByChain) {
        return null;
      }
      return {
        url: `${elastic.searchTermUrlByChain}v1/inventory/messages`,
        options: { headers, withCredentials: true },
      };
    }

    if (!elastic?.searchTermUrl) {
      return null;
    }

    return {
      url: `${elastic.searchTermUrl}v1/inventory/messages`,
      options: { headers },
    };
  }

  getFavoritesAvailabilityData(
    favPartNums: string[],
    boplExperienceEnabled: boolean,
    storeZipCode?: string,
    selectedStoreId?: string,
  ): Observable<ProductAvailabilityCollection | null> {
    if (!favPartNums?.length) {
      return of(null);
    }

    const config = this.getFavoritesAvailabilityRequestConfig();
    if (!config) {
      return throwError(() => new Error('Elastic searchTermUrl is not configured'));
    }
    const { url: inventoryRequest, options } = config;
    const params = {
      ecodeList: favPartNums,
      ...(selectedStoreId && { selectedStore: selectedStoreId }),
      ...(storeZipCode && { zipCode: storeZipCode }),
      ...(boplExperienceEnabled && { bopl: 'true' }),
    };

    const httpParams = new HttpParams().appendAll(params);
    const httpOptions: ProductCatalogHttpOptions = { ...options, params: httpParams };

    // Make the HTTP Request
    return this.http.get<ProductAvailabilityCollection>(inventoryRequest, httpOptions).pipe(
      timeout(5000),
      catchError((error) => throwError(() => error)),
    );
  }

  /**
   * @description
   * Get availability information for each ecode in the product list
   * @param productVOs
   */
  getProductPromoList(
    productVOs: ProductVO[] | OrchestratedProductAttributes[],
  ): Observable<ProductPromotionCollection> {
    const catentryIds = [
      ...new Set(
        productVOs.map((item) => {
          return item.parentCatentryId || item.catentryId;
        }),
      ),
    ];

    const promotions = this.environment.api.esb?.promotions;
    if (!promotions) {
      return throwError(() => new Error('ESB promotions configuration is not available'));
    }

    // Standard HTTP Headers
    let httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
    });

    // Custom HTTP Headers
    httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');
    httpHeaders = httpHeaders.set(CustomHttpHeaders.X_CENTRASITE_KEY, promotions.apiKey);
    httpHeaders = httpHeaders.set(CustomHttpHeaders.ENVIRONMENT, promotions.env);

    // Check platform
    if (!isPlatformBrowser(this.platformId)) {
      // Set a timeout on the server to prevent the response from taking too long
      httpHeaders = httpHeaders.set(
        CustomHttpHeaders.X_TIMEOUT,
        `${this.environment.api.esb?.gatewayUrlProxy?.timeout ?? ''}`,
      );
    }

    // Set http parameters
    const httpParams = new HttpParams()
      .set('catentryIds', `[${catentryIds}]`)
      .set('previewTime', promotions.previewTime)
      .set('channelId', promotions.channelId);

    // HTTP Options
    const httpOptions = {
      headers: httpHeaders,
      params: httpParams,
    };

    const gatewayUrl = this.environment.api.esb?.gatewayUrl;
    if (typeof gatewayUrl !== 'string' || !gatewayUrl.trim()) {
      return throwError(() => new Error('ESB gatewayUrl is not configured'));
    }

    // Make the HTTP Request
    return this.http
      .get(`${gatewayUrl}ws/WCS/v2/api/promotiondescription/${this.chain.storeId}`, httpOptions)
      .pipe(catchError((error) => throwError(error)));
  }
}
